import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Pause, Play, Square, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { fmtTime } from "@/lib/api";
import { useApp } from "@/lib/i18n";
import { useLiveTranscript } from "@/components/app/useLiveTranscript";
import { NoSystemAudioError, openSource, systemAudioSupport } from "@/components/app/audioSources";
import { useWakeLock } from "@/components/app/useWakeLock";
import { alertInterrupted } from "@/components/app/recordingAlert";
import { useMeetingForm } from "@/components/app/MeetingForm";

const DB = "minitai-rec";
const idb = (mode, fn) =>
  new Promise((resolve, reject) => {
    const open = indexedDB.open(DB, 1);
    open.onupgradeneeded = () => open.result.createObjectStore("chunks", { autoIncrement: true });
    open.onerror = () => reject(open.error);
    open.onsuccess = () => {
      const tx = open.result.transaction("chunks", mode);
      const req = fn(tx.objectStore("chunks"));
      tx.oncomplete = () => resolve(req?.result);
      tx.onerror = () => reject(tx.error);
    };
  });
const saveChunk = (b) => idb("readwrite", (s) => s.add(b)).catch(() => {});
const loadChunks = () => idb("readonly", (s) => s.getAll()).catch(() => []);
const clearChunks = () => idb("readwrite", (s) => s.clear()).catch(() => {});

const Ctx = createContext(null);
const RECORD_PATH = "/app/record";

function useRecorderEngine() {
  const { L } = useApp();
  const supported = typeof window !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof window.MediaRecorder !== "undefined";
  const [state, setState] = useState("idle");
  const wake = useWakeLock(state === "recording" || state === "paused");
  const [elapsed, setElapsed] = useState(0);
  const [analyser, setAnalyser] = useState(null);
  const [blob, setBlob] = useState(null);
  const [error, setError] = useState("");
  const [recovered, setRecovered] = useState(null);
  const [hiddenWarn, setHiddenWarn] = useState(false);
  const [stream, setStream] = useState(null);
  const [lost, setLost] = useState(null);
  const [gap, setGap] = useState(0);
  const eng = useRef({});
  const [mode, setModeState] = useState(() => (systemAudioSupport() === "ok" && localStorage.getItem("minitai-rec-source")) || "mic");
  const setMode = (m) => { localStorage.setItem("minitai-rec-source", m); setModeState(m); };
  const support = systemAudioSupport();
  const rec = useRef(null);
  const elapsedRef = useRef(0);
  elapsedRef.current = elapsed;
  const live = useLiveTranscript({ stream, state, elapsedRef, mode });

  useEffect(() => {
    loadChunks().then((c) => c?.length && setRecovered(new Blob(c, { type: "audio/webm" })));
  }, []);
  useEffect(() => {
    if (state !== "recording" && state !== "paused") return;
    const e = eng.current;
    const title = document.title;
    const t = setInterval(() => { if (state === "recording") setElapsed(Math.max(0, Math.floor(e.ctx.currentTime - e.base - e.paused))); }, 1000);
    const vis = () => {
      if (document.hidden) {
        setHiddenWarn(true);
        e.hiddenAt = performance.now(); e.audioAt = e.ctx.currentTime;
        document.title = state === "recording" ? L("🔴 Merakam… · MinitAI", "🔴 Recording… · MinitAI") : L("⏸ Dijeda · MinitAI", "⏸ Paused · MinitAI");
        return;
      }
      document.title = title;
      e.ctx.resume().catch(() => {});
      if (e.hiddenAt == null) return;
      const lostSec = (performance.now() - e.hiddenAt) / 1000 - (e.ctx.currentTime - e.audioAt);
      e.hiddenAt = null;
      if (state === "recording" && lostSec > 5) { setGap((g) => g + lostSec); alertInterrupted(); }
    };
    const unload = (ev) => { ev.preventDefault(); ev.returnValue = ""; };
    document.addEventListener("visibilitychange", vis);
    window.addEventListener("beforeunload", unload);
    return () => { clearInterval(t); document.title = title; document.removeEventListener("visibilitychange", vis); window.removeEventListener("beforeunload", unload); };
  }, [state, L]);

  const markLost = (reason) => {
    if (eng.current.mr?.state === "inactive") return;
    setLost((cur) => cur || { reason });
    if (!document.hidden) alertInterrupted();
  };
  const attach = (src) => {
    const e = eng.current;
    const node = e.ctx.createMediaStreamSource(src.stream);
    node.connect(e.an); node.connect(e.dest);
    e.src = src; e.node = node;
    src.watch.forEach((t) => {
      t.onended = () => markLost("ended");
      t.onmute = () => markLost("muted");
      t.onunmute = () => setLost((cur) => (cur?.reason === "muted" ? null : cur));
    });
    setStream(src.stream);
  };
  const reconnect = async () => {
    const e = eng.current;
    try {
      const src = await openSource(mode);
      e.node?.disconnect(); e.src?.stop();
      attach(src);
      await e.ctx.resume();
      setLost(null);
      toast.success(L("Rakaman disambung semula", "Recording resumed"));
    } catch (err) { toast.error(L("Tidak dapat menyambung semula: ", "Could not reconnect: ") + err.message); }
  };

  const start = async () => {
    setError("");
    let src;
    try {
      src = await openSource(mode);
      const ctx = new AudioContext();
      const an = ctx.createAnalyser();
      an.fftSize = 256;
      const dest = ctx.createMediaStreamDestination();
      const e = (eng.current = { ctx, an, dest, base: ctx.currentTime, paused: 0 });
      attach(src);
      await clearChunks();
      const chunks = [];
      const mr = new MediaRecorder(dest.stream, MediaRecorder.isTypeSupported("audio/webm") ? { mimeType: "audio/webm" } : undefined);
      e.mr = mr;
      mr.ondataavailable = (ev) => { if (ev.data.size) { chunks.push(ev.data); saveChunk(ev.data); } };
      mr.onstop = () => { e.src?.stop(); ctx.close(); setAnalyser(null); setStream(null); setLost(null); setBlob(new Blob(chunks, { type: mr.mimeType || "audio/webm" })); setState("stopped"); };
      ctx.onstatechange = () => { if (ctx.state !== "running" && ctx.state !== "closed" && mr.state === "recording") markLost("interrupted"); else if (ctx.state === "running") setLost((cur) => (cur?.reason === "interrupted" ? null : cur)); };
      mr.start(2000);
      rec.current = mr;
      live.reset();
      setAnalyser(an); setElapsed(0); setGap(0); setLost(null); setBlob(null); setHiddenWarn(false); setState("recording");
    } catch (e) {
      src?.stop();
      const sys = mode !== "mic" && !e.fromMic;
      const msg = e instanceof NoSystemAudioError ? L("Tiada audio dikongsi. Pilih tab/skrin semula dan tanda “Kongsi audio”.", "No audio was shared. Pick the tab/screen again and tick “Share audio”.")
        : e.name === "NotAllowedError" && sys ? L("Perkongsian skrin/audio dibatalkan atau ditolak.", "Screen/audio sharing was cancelled or denied.")
        : sys ? L("Audio sistem tidak dapat dimulakan: ", "Could not start system audio: ") + e.message
        : e.name === "NotAllowedError" ? L("Izin mikrofon ditolak. Benarkan akses mikrofon dalam tetapan pelayar.", "Microphone permission denied. Allow microphone access in browser settings.")
        : e.name === "NotFoundError" ? L("Tiada mikrofon dikesan pada peranti ini.", "No microphone found on this device.")
        : L("Mikrofon tidak dapat dimulakan: ", "Could not start microphone: ") + e.message;
      setError(msg);
      toast.error(msg);
    }
  };
  const pause = () => { rec.current.pause(); eng.current.pauseAt = eng.current.ctx.currentTime; setState("paused"); };
  const resume = () => { const e = eng.current; e.paused += e.ctx.currentTime - e.pauseAt; rec.current.resume(); setState("recording"); };
  const stop = () => rec.current.stop();
  const reset = async () => { await clearChunks(); live.reset(); setBlob(null); setElapsed(0); setState("idle"); };
  return { L, supported, state, setState, wake, elapsed, analyser, blob, setBlob, error, recovered, setRecovered, hiddenWarn, lost, gap, setGap,
    mode, setMode, support, live, start, pause, resume, stop, reset, reconnect, clearChunks };
}

const RecordingBar = ({ r }) => {
  const navigate = useNavigate();
  const { L } = r;
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4 lg:left-64" data-testid="recording-bar">
      <div className={`mx-auto flex max-w-2xl flex-wrap items-center gap-3 rounded-2xl border-2 bg-white p-3 shadow-2xl ${r.lost ? "border-red-400" : "border-red-200"}`}>
        <span className={`h-3 w-3 shrink-0 rounded-full ${r.state === "recording" ? "rec-pulse bg-red-500" : "bg-amber-400"}`} />
        <span className="flex-1 text-sm font-semibold text-slate-800" data-testid="recording-bar-status">
          {r.lost ? L("Rakaman terganggu", "Recording interrupted") : r.state === "recording" ? L("Sedang merakam", "Recording") : L("Dijeda", "Paused")} <span className="font-mono">{fmtTime(r.elapsed)}</span>
        </span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-9 rounded-xl" onClick={() => navigate(RECORD_PATH)} data-testid="recording-bar-back-btn"><ArrowLeft size={14} className="mr-1" />{L("Kembali", "Back")}</Button>
          {r.state === "recording"
            ? <Button size="sm" variant="outline" className="h-9 rounded-xl" onClick={r.pause} data-testid="recording-bar-pause-btn"><Pause size={14} className="mr-1" />{L("Jeda", "Pause")}</Button>
            : <Button size="sm" variant="outline" className="h-9 rounded-xl" onClick={r.resume} data-testid="recording-bar-resume-btn"><Play size={14} className="mr-1" />{L("Sambung", "Resume")}</Button>}
          <Button size="sm" className="h-9 rounded-xl bg-slate-900" onClick={() => { r.stop(); navigate(RECORD_PATH); }} data-testid="recording-bar-stop-btn"><Square size={13} className="mr-1" />{L("Henti", "Stop")}</Button>
        </div>
      </div>
    </div>
  );
};

export const RecorderProvider = ({ children }) => {
  const r = useRecorderEngine();
  const form = useMeetingForm();
  const loc = useLocation();
  const active = ["recording", "paused"].includes(r.state);
  return (
    <Ctx.Provider value={{ ...r, form }}>
      {children}
      {active && loc.pathname !== RECORD_PATH && <RecordingBar r={r} />}
    </Ctx.Provider>
  );
};

export const useRecorder = () => useContext(Ctx);
